import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type { EmployeeTaxaDto, EmployeeTaxaInput, EmployeeTaxaListItem } from './types';

/**
 * Fábrica do CRUD aninhado em um Colaborador. `employeeId` fecha no escopo.
 * Espelha o pattern de `createCompanyAddressesApi` em `empresa/addresses/api.ts`.
 *
 * Diferença chave: o id do filho é a `dataVigencia` (ISO `YYYY-MM-DD`) — string,
 * não number. Por isso o generic `TId = string` em `CrudApi`.
 */
export function createEmployeeTaxasApi(
  employeeId: number,
): CrudApi<EmployeeTaxaListItem, EmployeeTaxaDto, EmployeeTaxaInput, string> {
  const BASE = `/v1/employees/${employeeId}/taxas`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<EmployeeTaxaListItem>> {
      const { data } = await api.get<{
        data: Omit<EmployeeTaxaListItem, 'id'>[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params: toGenexusParams(query) });
      const normalized = normalizeListEnvelope(data);
      // PK do filho é `dataVigencia` (string); espelho em `id` p/ atender ao
      // contrato `T extends { id: string|number }` do DataTable.
      return {
        ...normalized,
        data: normalized.data.map((r) => ({ ...r, id: r.dataVigencia })),
      };
    },

    async get(dataVigencia: string): Promise<EmployeeTaxaDto> {
      const { data } = await api.get<{ data: EmployeeTaxaDto }>(`${BASE}/${dataVigencia}`);
      return data.data;
    },

    async create(input: EmployeeTaxaInput): Promise<EmployeeTaxaDto> {
      const { data } = await api.post<{ data: EmployeeTaxaDto }>(BASE, input);
      return data.data;
    },

    async update(dataVigencia: string, input: EmployeeTaxaInput): Promise<EmployeeTaxaDto> {
      // Server IGNORA `dataVigencia` do body (vem do path); mandamos mesmo assim por
      // simetria — o smoke e os testes proxy provam essa idempotência.
      const { data } = await api.put<{ data: EmployeeTaxaDto }>(`${BASE}/${dataVigencia}`, input);
      return data.data;
    },

    async remove(dataVigencia: string): Promise<void> {
      await api.delete(`${BASE}/${dataVigencia}`);
    },
  };
}
